import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { List, X } from "@phosphor-icons/react";
import { motion, AnimatePresence } from "framer-motion";

import axios from "axios";

const LOGO_URL = "/logo.png";
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = BACKEND_URL ? `${BACKEND_URL}/api` : "/api";

const navLinks = [
  { href: "/", label: "Home" },
  { href: "/about", label: "About" },
  { href: "/journals", label: "Journals" },
  { href: "/submit-manuscript", label: "Submit Manuscript" },
  { href: "/editorial-board", label: "Editorial Board" },
  { href: "/contact", label: "Contact" },
  { href: "/admin", label: "Admin" },
];

export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [announcement, setAnnouncement] = useState("Welcome to iScience Publishers — Submissions for 2026 Volume are now open!");
  const location = useLocation();

  useEffect(() => {
    const fetchAnnouncement = async () => {
      try {
        const res = await axios.get(`${API}/site-content`);
        if (res.data && typeof res.data === "object" && res.data.announcement) {
          setAnnouncement(res.data.announcement);
        }
      } catch (err) {
        // use default
      }
    };
    fetchAnnouncement();
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location]);

  return (
    <>
      {announcement && (
        <div className="bg-[#0F172A] text-white py-1.5 px-4 text-center text-xs font-semibold tracking-wide border-b border-[#1E293B]">
          <span className="bg-[#0D9488] text-white px-2 py-0.5 rounded-xs mr-2 text-[10px] uppercase font-bold">Notice</span>
          {announcement}
        </div>
      )}
      <header
        data-testid="main-header"
        className={`sticky top-0 z-50 bg-white/95 backdrop-blur-xl border-b transition-all duration-300 ${
          isScrolled ? "border-[#E2E8F0] shadow-sm" : "border-transparent"
        }`}
      >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 sm:h-20">
          {/* Logo */}
          <Link
            to="/"
            data-testid="logo-link"
            className="flex items-center gap-3 flex-shrink-0"
          >
            <img
              src={LOGO_URL}
              alt="iScience Publishers"
              className="h-16 sm:h-20 w-auto object-contain"
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-8" data-testid="desktop-nav">
            {navLinks.map((link) => (
              <Link
                key={link.href}
                to={link.href}
                data-testid={`nav-${link.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={`text-sm font-semibold transition-colors relative py-2 ${
                  location.pathname === link.href
                    ? "text-[#0D9488]"
                    : "text-[#334155] hover:text-[#0D9488]"
                }`}
              >
                {link.label}
                {location.pathname === link.href && (
                  <motion.div
                    layoutId="activeNav"
                    className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#0D9488]"
                    transition={{ type: "spring", stiffness: 380, damping: 30 }}
                  />
                )}
              </Link>
            ))}
          </nav>

          {/* Mobile Menu Button */}
          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="lg:hidden p-2 text-[#334155] hover:text-[#0D9488] transition-colors"
            aria-label="Toggle menu"
          >
            {isMobileMenuOpen ? <X size={24} weight="bold" /> : <List size={24} weight="bold" />}
          </button>
        </div>
      </div>

      {/* Mobile Navigation */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.nav
            data-testid="mobile-nav"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.2 }}
            className="lg:hidden bg-white border-t border-[#E2E8F0] overflow-hidden"
          >
            <div className="px-4 py-4 space-y-1">
              {navLinks.map((link) => (
                <Link
                  key={link.href}
                  to={link.href}
                  data-testid={`mobile-nav-${link.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className={`block px-4 py-3 rounded-sm text-sm font-semibold transition-colors ${
                    location.pathname === link.href
                      ? "bg-[#F0FDFA] text-[#0D9488]"
                      : "text-[#334155] hover:bg-[#F8FAFC]"
                  }`}
                >
                  {link.label}
                </Link>
              ))}
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  </>
  );
}
